import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Zap, Target, Users, Trophy, Clock, CheckCircle, Dumbbell, Timer, Flame, Shield } from "lucide-react";

import { MapPin } from "lucide-react";

const LOGO_SPORTS = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/xqwv9ghf_IMG_7268.png";

const TrainingPage = () => {
  const individualTraining = {
    title: "1-on-1 Training",
    subtitle: "Fully customized sessions tailored to your athlete's age, sport, and goals.",
    price: "$55",
    duration: "60 min",
    packages: [
      { name: "4-Session Package", price: "$200", savings: "Save $20" },
      { name: "8-Session Package", price: "$380", savings: "Save $60" }
    ],
    features: ["Individualized programming", "Progress tracking", "Focus on proper movement & technique"]
  };

  const smallGroupTraining = {
    title: "Small Group Training (3-6 Athletes)",
    subtitle: "Perfect for teammates & friends.",
    options: [
      { name: "Drop-In", price: "$25", note: "per athlete" },
      { name: "5 Sessions", price: "$110", note: "per athlete" },
      { name: "10 Sessions", price: "$200", note: "per athlete" }
    ],
    features: ["Competitive & motivating environment", "Best value option", "Most popular program"]
  };

  const positionTraining = {
    title: "Sport-Specific / Position Training",
    options: [
      { name: "Small Group Position Work", price: "$30", note: "per athlete" },
      { name: "Private Position Session", price: "$65" }
    ]
  };

  const memberships = [
    {
      name: "BASIC TRAINING",
      price: "$200",
      period: "/month",
      features: ["2 sessions per week", "Speed & Agility Focus"],
      popular: false
    },
    {
      name: "COMPLETE ATHLETE",
      price: "$280",
      period: "/month",
      features: ["3 sessions per week", "Speed + Strength + Agility", "Sport-Specific training"],
      popular: true
    },
    {
      name: "ELITE DEVELOPMENT",
      price: "$350",
      period: "/month",
      features: ["Unlimited group sessions", "Performance tracking", "Priority scheduling"],
      popular: false
    }
  ];

  const addOns = [
    { name: "Vertical Jump Testing", price: "$25" },
    { name: "40-Yard Dash Testing", price: "$25" },
    { name: "Customized At-Home Workout Plan", price: "$50" },
    { name: "Nutrition Guidance", price: "$75" }
  ];

  const teamPackages = [
    { name: "Single Session", price: "$200", note: "Up to 15 athletes" },
    { name: "4 Sessions", price: "$700" },
    { name: "8 Sessions", price: "$1,300" }
  ];

  const teamFeatures = ["Pre-season & in-season options", "Strength, speed, and conditioning", "Coach collaboration available"];

  return (
    <div className="min-h-screen" data-testid="training-page">
      {/* Hero */}
      <section className="hero-bg py-24 relative">
        <div className="absolute inset-0 athletic-pattern"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="max-w-3xl">
              <p className="text-[#E10600] font-bold uppercase tracking-widest mb-4">Elite Sports Performance Training</p>
              <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-white mb-4">
                YOUTH<br /><span className="text-[#E10600]">TRAINING</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-2">Speed • Agility • Strength • Sports-Specific Development</p>
              <p className="text-[#E10600] font-semibold text-base md:text-lg mb-6">Youth | Middle School | High School</p>
              <p className="text-gray-400 mb-8 max-w-xl text-sm md:text-base">
                We provide safe, professional, and results-driven training for youth athletes of all levels. Our programs focus on long-term athletic development, injury risk reduction, and performance gains that transfer to sport.
              </p>
              <Link to="/booking?category=training" data-testid="training-hero-book">
                <Button className="cta-btn-primary text-base md:text-lg">
                  Book Training Session
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="hidden md:block">
              <img src={LOGO_SPORTS} alt="Top Notch Sports" className="h-48 lg:h-64 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-12 bg-[#111111] border-y border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="w-16 h-16 rounded-full bg-[#E10600]/20 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-8 h-8 text-[#E10600]" />
            </div>
            <div>
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Training Location</p>
              <h3 className="font-display text-2xl md:text-3xl text-white">GT Community Park</h3>
              <p className="text-gray-400">Gloucester Township, NJ</p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Benefits */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-6 h-6" />, text: "Safe & Professional" },
              { icon: <Target className="w-6 h-6" />, text: "Results-Driven" },
              { icon: <Zap className="w-6 h-6" />, text: "Long-Term Development" },
              { icon: <Trophy className="w-6 h-6" />, text: "Performance Gains" }
            ].map((item, index) => (
              <div key={index} className="feature-card" data-testid={`benefit-card-${index}`}>
                <div className="feature-icon mx-auto">
                  {item.icon}
                </div>
                <p className="text-white font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Training */}
      <section className="py-20 bg-[#111111]" data-testid="individual-training">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-12">1-ON-1 TRAINING</h2>
          
          <Card className="bg-[#1a1a1a] border-[#2A2A2A] max-w-2xl mx-auto">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-[#E10600] flex items-center justify-center mx-auto mb-4">
                  <Dumbbell className="w-8 h-8 text-white" />
                </div>
                <p className="text-gray-400">{individualTraining.subtitle}</p>
              </div>
              
              <div className="text-center mb-6">
                <p className="text-gray-500 text-sm">Single Session ({individualTraining.duration})</p>
                <span className="text-[#E10600] font-bold text-4xl">{individualTraining.price}</span>
              </div>
              
              <div className="border-t border-[#2A2A2A] pt-6 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  {individualTraining.packages.map((pkg, index) => (
                    <div key={index} className="bg-[#111111] rounded-lg p-4 text-center">
                      <p className="text-white font-medium text-sm">{pkg.name}</p>
                      <p className="text-[#E10600] font-bold text-xl">{pkg.price}</p>
                      <span className="text-green-400 text-xs">({pkg.savings})</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2 mb-6">
                {individualTraining.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#E10600] flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>
              
              <div className="text-center">
                <Link to="/booking?category=training">
                  <Button className="cta-btn-primary">Book Session</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Small Group & Position Training */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Small Group */}
            <Card className="bg-[#111111] border-[#2A2A2A] border-2 border-[#E10600]/50" data-testid="small-group-training">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-full bg-[#E10600]/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#E10600]" />
                  </div>
                  <span className="bg-[#E10600] text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</span>
                </div>
                <CardTitle className="font-display text-2xl text-white mt-4">{smallGroupTraining.title}</CardTitle>
                <p className="text-gray-400 text-sm">{smallGroupTraining.subtitle}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  {smallGroupTraining.options.map((option, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-[#2A2A2A] last:border-0">
                      <span className="text-gray-300">{option.name}</span>
                      <div className="text-right">
                        <span className="text-[#E10600] font-bold">{option.price}</span>
                        {option.note && <span className="text-gray-500 text-xs ml-1">{option.note}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {smallGroupTraining.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#E10600] flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Position-Specific */}
            <Card className="bg-[#111111] border-[#2A2A2A]" data-testid="position-training">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-[#E10600]/20 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-[#E10600]" />
                </div>
                <CardTitle className="font-display text-2xl text-white">{positionTraining.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {positionTraining.options.map((option, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-[#2A2A2A] last:border-0">
                      <span className="text-gray-300">{option.name}</span>
                      <div className="text-right">
                        <span className="text-[#E10600] font-bold text-lg">{option.price}</span>
                        {option.note && <span className="text-gray-500 text-xs ml-1">{option.note}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Monthly Memberships */}
      <section className="py-20 bg-[#111111]" data-testid="memberships">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-4">MONTHLY MEMBERSHIP OPTIONS</h2>
          <p className="text-gray-400 text-center mb-12">Commit to your development and save</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {memberships.map((membership, index) => (
              <Card 
                key={index} 
                className={`relative ${membership.popular ? 'bg-gradient-to-b from-[#E10600]/20 to-[#111111] border-[#E10600]' : 'bg-[#1a1a1a] border-[#2A2A2A]'}`}
                data-testid={`membership-${index}`}
              >
                {membership.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#E10600] text-white text-xs font-bold px-4 py-1 rounded-full">BEST VALUE</span>
                  </div>
                )}
                <CardContent className="p-6 pt-8">
                  <h3 className="font-display text-xl text-white text-center mb-2">{membership.name}</h3>
                  <div className="text-center mb-6">
                    <span className="text-[#E10600] font-bold text-4xl">{membership.price}</span>
                    <span className="text-gray-400">{membership.period}</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {membership.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#E10600] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button className={`w-full ${membership.popular ? 'cta-btn-primary' : 'bg-[#2A2A2A] hover:bg-[#3A3A3A]'}`}>
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Training */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="team-training">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-4">TEAM TRAINING PACKAGES</h2>
          <p className="text-gray-400 text-center mb-12">Customized programs for school teams and clubs</p>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            {teamPackages.map((pkg, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-[#2A2A2A] hover:border-[#E10600] transition-all text-center">
                <CardContent className="p-6">
                  <h3 className="font-display text-xl text-white mb-2">{pkg.name}</h3>
                  <p className="text-[#E10600] font-bold text-3xl mb-2">{pkg.price}</p>
                  {pkg.note && <p className="text-gray-500 text-sm">{pkg.note}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {teamFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-300 text-sm bg-[#111111] px-4 py-2 rounded-full">
                <CheckCircle className="w-4 h-4 text-[#E10600]" />
                {feature}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-white text-center mb-8">ADD-ONS</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {addOns.map((addon, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-[#2A2A2A] hover:border-[#E10600] transition-all">
                <CardContent className="p-4 text-center">
                  <p className="text-white font-medium text-sm mb-2">{addon.name}</p>
                  <span className="text-[#E10600] font-bold text-lg">{addon.price}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Deposit Notice */}
      <section className="py-6 bg-[#E10600]/10 border-y border-[#E10600]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white font-medium">
            <span className="text-[#E10600] font-bold">20% deposit required</span> upon booking for all services • Secure online payment
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#E10600]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
            READY TO ELEVATE YOUR GAME?
          </h2>
          <p className="text-white/80 mb-8">
            Join the athletes who are training with intention. Book your first session today.
          </p>
          <Link to="/booking?category=training" data-testid="training-cta-book">
            <Button className="bg-white text-[#0A0A0A] hover:bg-gray-100 font-bold text-lg px-8 py-6">
              BOOK TRAINING SESSION
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TrainingPage;
